/**
 * RepaymentArea — "How is it repaid, and from when?"
 *
 * EVERY ADVANCE IS REPAID WEEKLY, AND THE REPAYMENT IS ADDED TO THE CONTRACT INSTALMENT [D-B54]. The
 * side sheet this replaces put that behind a "Separate repayment schedule" switch whose OFF state
 * wrote no `loan_instalments` row at all — 47 of the 161 production advances are in exactly that
 * state, billing nothing while standing in `total_value`. The switch is gone: the weekly figure and
 * the day it starts are both required, and a half-written schedule is refused rather than silently
 * skipped (`validateDraft`).
 *
 * THE PANEL IS THE CONSEQUENCE, STATED BEFORE IT IS COMMITTED. Typing 200 here raises what the
 * driver owes every Monday from the first repayment's week, and the figure it becomes is composed
 * HERE — the latest contract instalment, every insurance premium advance's current rate, every
 * other standing advance's current rate, and the figure being typed. `weekly_rate` is deliberately
 * not read for it: that number answers with the LAST REPAYMENT'S allocator total where one exists,
 * so on a contract that has been paying for months it states what was billed last week rather than
 * what will be billed from this advance's first Monday. `advanceArithmetic.ts` carries the whole
 * argument.
 *
 * AND THE APP'S OWN WEEKLY FIGURE CATCHES UP LATER, WHICH IS WORTH SAYING OUT LOUD [D-B54]. The
 * overdue engine expects the raised amount from the first instalment's Monday, while the tile at
 * the top of Contract Insights re-derives from the last repayment's allocation — so between those
 * two events the two numbers legitimately disagree, and an admin who is not told that reads it as
 * a save that did not land.
 *
 * TWO STATES WHERE NOTHING CAN BE TYPED, AND EACH SAYS WHY. [D-B54] — an advance below the recovery
 * watermark with no schedule is settled at completion, and adding one now would admit it to the
 * allocator at every repayment since disbursement and re-split history across 32 contracts. [F7] —
 * a signed Schedule's figures are frozen, and the correction is void-and-reissue.
 */

import type { ReactNode } from 'react';
import { format } from 'date-fns';

import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency, parseBusinessDay } from '@/lib/format';

import { Block, Blocks, Hint, LockLine, Rec, Rows } from '../../areas/blocks';
import { advanceMoney, derivedTerm, type WeeklyInstalmentRow } from '../advanceArithmetic';
import { advanceDraftOf, type AdvanceFormValues } from '../advanceForm';
import type { AdvanceLegacyReason } from '../advanceState';
import ForwardWeeklyPanel from './ForwardWeeklyPanel';
import { FROZEN_LOCK_BODY, FROZEN_LOCK_TITLE } from './lockCopy';

/** [D-B54]'s own sentence, and it is a statement of fact rather than a refusal. */
const PREDATES_THE_RULE = 'Settled at completion — this advance predates the weekly-schedule rule.';

/** The same shut door where the watermark could not be read — a state, not a fact about the money. */
const WATERMARK_UNKNOWN =
    'The recovery watermark could not be read, so no repayment can be set here yet.';

/** And the same door again where the read that CARRIES the schedule is the one that failed. */
const BOARD_UNREADABLE =
    'The agreements board could not be loaded, so this advance’s repayment cannot be read here yet.';

interface Props {
    mode: 'create' | 'edit';
    /** The Schedule is signed: every figure below is a read [F7]. */
    frozen: boolean;
    /** [D-B54] — never scheduled and no write offered, with the reason the copy turns on. */
    legacyNoSchedule: AdvanceLegacyReason | false;
    form: AdvanceFormValues;
    onChange: (updates: Partial<AdvanceFormValues>) => void;
    /** The forward sum's OTHER terms — this advance is never among them. */
    forward: {
        contractInstalment: number | null;
        insuranceLoanInstalments: WeeklyInstalmentRow[];
        loanInstalments: WeeklyInstalmentRow[];
    };
    /** The create bar, or the edit bar for this area. Drawn by the page. */
    commit?: ReactNode;
}

function money(value: number | null): string | null {
    return value === null || value === undefined ? null : formatCurrency(value);
}

export default function RepaymentArea({
    mode,
    frozen,
    legacyNoSchedule,
    form,
    onChange,
    forward,
    commit,
}: Props) {
    const draft = advanceDraftOf(form);
    const { total } = advanceMoney(draft.amount, draft.interestRate ?? 0);
    const term = derivedTerm(total, draft.weeklyAmount);

    /*
     * Parsed field by field, so the calendar opens on the day the column holds — and `setDate`
     * writes that same day straight back out. `loan_instalments.starts_at` is a `date` column, so
     * the string discipline is the whole of it: `new Date('2026-08-01')` is UTC midnight read back
     * with LOCAL getters, which walked `starts_at` back a day on every open-and-save at any
     * negative offset, and compounded.
     */
    const startsOn = parseBusinessDay(form.firstStartsAt);

    if (legacyNoSchedule) {
        /*
         * ONE SHUT DOOR, THREE SENTENCES. Below the watermark the advance IS settled at completion,
         * and the panel states it. Where the watermark row was absent or its read refused, the door
         * is shut for the same conservative reason — no write is offered — but the app has NOT
         * established that this advance predates the rule, and a money screen that says so anyway is
         * the one thing this dossier exists not to do.
         *
         * AND WHERE THE BOARD ITSELF WAS REFUSED, THE APP HAS REASON TO DOUBT IT. The instalment
         * rows are read off the board's subjects, so a failed board read hands this canvas an empty
         * schedule that is indistinguishable from an advance that never had one. 114 of the 161
         * production advances DO have a schedule and every one of them is below the watermark, so
         * "Settled at completion" here would be a false statement about a live weekly repayment on
         * the overwhelming majority of them. It is a state, and it says so.
         */
        const lede =
            legacyNoSchedule === 'board-unreadable'
                ? BOARD_UNREADABLE
                : legacyNoSchedule === 'watermark-unknown'
                  ? WATERMARK_UNKNOWN
                  : PREDATES_THE_RULE;
        const empty =
            legacyNoSchedule === 'board-unreadable'
                ? 'Not read'
                : legacyNoSchedule === 'watermark-unknown'
                  ? 'Not established'
                  : 'Settled at completion';
        return (
            <Blocks>
                <Block id="b-repayment" title="Repayment" aside="records, not requirements">
                    <p className="sd-lede sd-dim">{lede}</p>
                    <Rows>
                        <Rec label="Weekly repayment" value={null} empty={empty} />
                        <Rec label="First repayment" value={null} empty={empty} />
                    </Rows>
                    {/* THE DOOR IS SHUT RATHER THAN GUARDED [D-B54]. Adding a schedule to an advance
                        below the recovery watermark admits it to the allocator at every repayment
                        since disbursement and re-splits history — 32 contracts and ~GHS 139,858 of
                        it, across 45 recorded partner payouts. The side sheet allowed it; that door
                        closed with the sheet. */}
                    <Hint className="mt-3">
                        {legacyNoSchedule === 'board-unreadable' ? (
                            <>
                                This advance’s repayment schedule is read off the agreements board,
                                and that read was refused — so an empty schedule here is not
                                evidence that there is none. Nothing is offered until it answers,
                                because a weekly figure written now would be planned against rate
                                rows the page never had in hand. Reload the page to try again.
                            </>
                        ) : legacyNoSchedule === 'watermark-unknown' ? (
                            <>
                                Whether this advance falls under the weekly-schedule rule is decided
                                by the recovery watermark, and that read has not answered. Until it
                                does, nothing is offered here — setting a repayment on an advance
                                that predates the rule would re-split every payment the contract has
                                made. Reload the page to try the read again.
                            </>
                        ) : (
                            <>
                                Advances opened before the weekly-schedule rule are repaid out of
                                the contract’s own instalment and settled when it completes. A
                                weekly figure added now would be billed from the day the money was
                                disbursed, re-splitting every repayment this contract has made since
                                — so this is a record here, not a field.
                            </>
                        )}
                    </Hint>
                </Block>
            </Blocks>
        );
    }

    return (
        <Blocks>
            <Block id="b-repayment" title="Repayment" aside="required">
                {frozen && <LockLine title={FROZEN_LOCK_TITLE}>{FROZEN_LOCK_BODY}</LockLine>}

                <p className="sd-lede sd-dim">
                    Every advance is repaid weekly, and its repayment is added to the contract
                    instalment. From the Monday of the first repayment’s week the driver owes the
                    raised total, and the Schedule states both figures.
                </p>

                {frozen ? (
                    <Rows>
                        <Rec
                            label="Weekly repayment"
                            value={money(draft.weeklyAmount)}
                            empty="Not set"
                        />
                        <Rec
                            label="First repayment"
                            value={startsOn ? format(startsOn, 'MMM d, yyyy') : null}
                            empty="Not set"
                        />
                    </Rows>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label
                                className="text-xs uppercase tracking-wide text-muted-foreground"
                                htmlFor="f-weekly"
                            >
                                Weekly repayment (GH₵)
                            </Label>
                            <Input
                                id="f-weekly"
                                type="number"
                                step="0.01"
                                min="0"
                                className="h-11 text-lg"
                                value={form.weeklyAmount}
                                onChange={(event) => onChange({ weeklyAmount: event.target.value })}
                                placeholder="e.g. 200"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label
                                className="text-xs uppercase tracking-wide text-muted-foreground"
                                htmlFor="f-first"
                            >
                                First repayment
                            </Label>
                            <div id="f-first" tabIndex={-1}>
                                <DatePicker
                                    date={startsOn ?? undefined}
                                    setDate={(date) =>
                                        onChange({
                                            firstStartsAt: date ? format(date, 'yyyy-MM-dd') : '',
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Block>

            {/* THE SAME PANEL THE INSURANCE DOSSIER DRAWS, AND THE LABELS ARE WHAT DIFFER. This
                canvas's `forward` excludes this advance and nothing else, so its two Σ rows are
                every insurance premium advance and every OTHER advance — the words they have
                always carried. See `ForwardWeeklyPanel`. */}
            <ForwardWeeklyPanel
                forward={forward}
                typed={draft.weeklyAmount}
                insuranceLoansLabel="Insurance premium advances"
                loansLabel="Other advances"
            >
                {/* THE TERM IS DERIVED, NEVER STORED. `termWeeks` floors and adds a final week
                    only where the part-week is worth half a pesewa or more [D-T1] — the same
                    guard `agreement_merge_data` applies to `{{contract.term_weeks}}` — and the
                    last repayment is the total MINUS what the full weeks cover, never a
                    percentage or a remainder. */}
                <Rec
                    label="Expected term"
                    value={term.term === null ? null : `${term.term} weeks`}
                    sub={
                        term.finalInstalment === null
                            ? undefined
                            : `final repayment ${formatCurrency(term.finalInstalment)}`
                    }
                    empty="Needs the total repayable and a weekly figure"
                />
            </ForwardWeeklyPanel>

            {mode === 'create' && (
                <Hint>
                    Creating the advance writes the `loans` row and this schedule together. Neither
                    lands without the other.
                </Hint>
            )}

            {commit}
        </Blocks>
    );
}
